import { useState } from 'react';
import { ApiError } from '../api/client';
import { type CreatedApiKey, useApiKeys, useCreateApiKey, useRevokeApiKey } from '../api/apiKeys';
import { Field } from '../components/Field';
import { I } from '../components/Icons';
import { Modal } from '../components/Modal';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

const CREATE_ERRORS: Record<string, string> = {
  invalid_request: 'Enter a name for the key.',
  rate_limited: 'Too many keys created. Try again in a minute.',
};

export function ApiKeysList() {
  const keys = useApiKeys();
  const revoke = useRevokeApiKey();
  const [creating, setCreating] = useState(false);

  function handleRevoke(id: string, name: string) {
    if (!window.confirm(`Revoke the key “${name}”? Any integration using it will stop working.`)) {
      return;
    }
    revoke.mutate(id);
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>API keys</h1>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            New key
          </button>
        </div>
      </div>

      <p className="micro" style={{ maxWidth: 640, marginBottom: 16 }}>
        API keys grant read-only access to reporting endpoints (sales, refunds, disputes, CSV
        exports) for the events you can see. Send the key as{' '}
        <code>Authorization: Bearer ik_…</code>. Keys never expire — revoke one to cut off access.
      </p>

      {keys.isPending && <div className="loading">Loading…</div>}
      {keys.isError && <div className="error">Could not load API keys.</div>}
      {keys.data && keys.data.length === 0 && <div className="empty">No API keys yet.</div>}
      {keys.data && keys.data.length > 0 && (
        <div className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Created by</th>
                <th>Last used</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {keys.data.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td>
                    <code>{k.prefix}…</code>
                  </td>
                  <td>{k.createdByEmail ?? '—'}</td>
                  <td>{fmtDate(k.lastUsedAt)}</td>
                  <td>
                    {k.revokedAt ? (
                      <span className="pill pill-ended">revoked</span>
                    ) : (
                      <span className="pill pill-paid">active</span>
                    )}
                  </td>
                  <td>
                    {!k.revokedAt && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        style={{ color: 'var(--oeg-danger)' }}
                        onClick={() => handleRevoke(k.id, k.name)}
                        disabled={revoke.isPending}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateApiKeyModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function CreateApiKeyModal({ onClose }: { onClose: () => void }) {
  const create = useCreateApiKey();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) {
      setError('Enter a name for the key.');
      return;
    }
    try {
      const key = await create.mutateAsync({ name: name.trim() });
      setCreated(key);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      setError(CREATE_ERRORS[code] ?? 'Could not create the key. Try again.');
    }
  }

  async function handleCopy() {
    if (!created) return;
    try {
      await navigator.clipboard?.writeText(created.token);
      setCopied(true);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — the token is still
      // selectable on screen.
    }
  }

  // Reveal state: the token is shown exactly once.
  if (created) {
    return (
      <Modal
        title="API key created"
        sub="Copy this token now — it won't be shown again."
        onClose={onClose}
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <Field label="Token">
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <input readOnly value={created.token} style={{ fontFamily: 'monospace', flex: 1 }} />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleCopy}
              style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
            >
              <I.copy size={16} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </Field>
      </Modal>
    );
  }

  return (
    <Modal
      title="New API key"
      sub="Read-only access to your reporting endpoints."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="create-api-key-form"
            className="btn btn-primary"
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      <form id="create-api-key-form" onSubmit={handleSubmit}>
        <Field label="Name" help="A label to recognize this key later, e.g. “QuickBooks sync”.">
          <input
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            autoFocus
          />
        </Field>
        {error && <div className="form-error">{error}</div>}
      </form>
    </Modal>
  );
}
